import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";
import { Wishlist } from "./wishlist-store";

const HANDLE_ATTRIBUTE = "data-wishlist-handle";

/**
 * Pulls the product handle out of a product URL, ignoring the query string and any locale prefix
 * (e.g. `https://shop.example/en-au/products/foo?section_id=bar` yields `foo`).
 */
function handleFromUrl(url: string): string | null {
  let pathname: string;

  try {
    pathname = new URL(url, window.location.origin).pathname;
  } catch {
    return null;
  }

  const match = /\/products\/([^/]+)/.exec(pathname);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/**
 * Renders the wishlist page's product grid.
 *
 * The wishlist lives in localStorage, so there is nothing for Liquid to loop over at render time.
 * Each stored handle is instead fetched through the Section Rendering API against the product's
 * own URL (`/products/<handle>?section_id=<this section>`), which returns a single product card
 * built from this section instance's own `_product-card` block configuration.
 */
class WishlistProducts extends Component {
  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.wishlistUpdate, this.#onUpdate);
    this.#renderAll();
  }

  updatedCallback(): void {
    super.updatedCallback();
    // The Section Rendering API morphs this section in place (e.g. after a block-config change in
    // the theme editor), resetting the container back to its server-rendered (empty) markup
    // without reconnecting the element. Reuse the same diff #onUpdate uses, rather than a full
    // wipe-and-refetch, so a card that happens to survive the morph isn't needlessly refetched.
    this.#syncTo(Wishlist.getHandles());
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.wishlistUpdate, this.#onUpdate);
  }

  get #container(): HTMLElement | null {
    return this.querySelector<HTMLElement>(".main-wishlist__grid");
  }

  #renderedHandles(): string[] {
    const container = this.#container;
    if (!container) return [];
    return Array.from(
      container.querySelectorAll<HTMLElement>(`[${HANDLE_ATTRIBUTE}]`),
    ).map((card) => card.dataset.wishlistHandle ?? "");
  }

  #onUpdate = (event: Event): void => {
    const { handles } = (event as CustomEvent<{ handles: string[] }>).detail;
    this.#syncTo(handles);
  };

  /**
   * Reconciles the rendered cards to exactly match `handles`: drops any card whose handle is no
   * longer in the wishlist, and fetches a card for any handle that isn't rendered yet. Existing,
   * still-valid cards are left untouched rather than refetched.
   */
  #syncTo(handles: string[]): void {
    const container = this.#container;
    if (!container) return;

    container
      .querySelectorAll<HTMLElement>(`[${HANDLE_ATTRIBUTE}]`)
      .forEach((card) => {
        if (!handles.includes(card.dataset.wishlistHandle ?? ""))
          this.#removeCard(card);
      });

    const rendered = new Set(this.#renderedHandles());
    handles
      .filter((handle) => !rendered.has(handle))
      .forEach((handle) => this.#renderCard(handle));

    this.#syncEmptyState(handles.length);
  }

  #renderAll(): void {
    const container = this.#container;
    if (!container) return;

    container.replaceChildren();

    const handles = Wishlist.getHandles();
    this.#syncEmptyState(handles.length);
    handles.forEach((handle) => this.#renderCard(handle));
  }

  #syncEmptyState(count: number): void {
    this.classList.toggle("is-empty", count === 0);
  }

  /**
   * Appends a placeholder for `handle` straight away — keeping grid order stable regardless of the
   * order the network responds in — then loads the real card into it.
   */
  #renderCard(handle: string): void {
    const container = this.#container;
    if (!container || !this.#sectionId) return;

    const wrapper = document.createElement("div");
    wrapper.className = "resource-list__item";
    wrapper.dataset.wishlistHandle = handle;
    container.appendChild(wrapper);

    void this.#loadCard(wrapper, handle);
  }

  get #sectionId(): string | undefined {
    return this.closest<HTMLElement>("[data-section-id]")?.dataset.sectionId;
  }

  async #loadCard(wrapper: HTMLElement, handle: string): Promise<void> {
    const sectionId = this.#sectionId;
    if (!sectionId) return;

    this.#renderSkeleton(wrapper);

    let response: Response;

    try {
      response = await fetch(
        `/products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(sectionId)}`,
      );
    } catch {
      // Network error (offline, DNS, timeout) — transient, so keep the handle and offer a retry.
      this.#renderRetry(wrapper, handle);
      return;
    }

    if (response.status === 404) {
      this.#forget(wrapper, handle);
      return;
    }

    if (!response.ok) {
      // Any other error status (5xx, throttling) is transient — keep the handle, offer a retry.
      this.#renderRetry(wrapper, handle);
      return;
    }

    if (this.#migrateRenamedHandle(response, handle)) return;

    const document_ = new DOMParser().parseFromString(
      await response.text(),
      "text/html",
    );

    // Shopify answers a section request for a missing product with HTTP 200 and a 404 page
    // context, so the status code can't be relied on alone — main-wishlist.liquid renders an
    // explicit marker for that case.
    if (document_.querySelector("[data-wishlist-product-missing]")) {
      this.#forget(wrapper, handle);
      return;
    }

    const card = document_.querySelector("product-card");

    if (!card) {
      // A 200 that is neither a card nor the missing marker is unexpected — treat it as
      // transient rather than silently discarding a product the shopper saved.
      this.#renderRetry(wrapper, handle);
      return;
    }

    wrapper.replaceChildren(card);
  }

  /**
   * The product is gone (deleted or unpublished) and isn't coming back — self-heal by dropping it
   * from storage. Removing the handle re-enters #onUpdate, which removes the wrapper for us, but
   * remove it here too in case this element is already detached.
   */
  #forget(wrapper: HTMLElement, handle: string): void {
    Wishlist.removeHandle(handle);
    this.#removeCard(wrapper);
  }

  /**
   * Removes a card wrapper from the grid, first moving focus off it if it currently contains
   * focus (e.g. the user just activated the wishlist-toggle button that's about to disappear) —
   * otherwise focus would silently reset to <body>, per this repo's focus-management rules.
   * Prefers the next card's wishlist-toggle button, then the previous card's, then falls back to
   * the grid container itself for the case where the removed card was the only one left.
   */
  #removeCard(wrapper: HTMLElement): void {
    if (wrapper.contains(document.activeElement)) {
      this.#focusableNear(wrapper)?.focus();
    }
    wrapper.remove();
  }

  #focusableNear(wrapper: HTMLElement): HTMLElement | null {
    return (
      this.#toggleButtonIn(wrapper.nextElementSibling) ??
      this.#toggleButtonIn(wrapper.previousElementSibling) ??
      this.#focusFallback()
    );
  }

  #toggleButtonIn(element: Element | null): HTMLElement | null {
    if (!(element instanceof HTMLElement)) return null;
    return element.querySelector<HTMLElement>(".wishlist-toggle__button");
  }

  /**
   * Last resort when there's no sibling card to focus (the removed card was the only one in the
   * grid): focus the grid container itself, giving it tabindex="-1" on demand so it's
   * programmatically focusable without joining the normal tab order.
   */
  #focusFallback(): HTMLElement | null {
    const container = this.#container;
    if (!container) return null;
    if (!container.hasAttribute("tabindex"))
      container.setAttribute("tabindex", "-1");
    return container;
  }

  /**
   * Shopify redirects a renamed product's old handle to its new one, so the fetch still succeeds —
   * but the card it returns is keyed to the new handle while storage still holds the old one. That
   * mismatch leaves the card's heart looking un-wishlisted and makes clicking it add a duplicate
   * instead of removing, stranding the old handle forever. Rewrite storage instead.
   *
   * Returns true when a rename was handled: the two writes below each fire a wishlistUpdate, and
   * #onUpdate drops the stale wrapper and renders a fresh card under the new handle, so this
   * call's own response is no longer needed.
   */
  #migrateRenamedHandle(response: Response, handle: string): boolean {
    if (!response.redirected) return false;

    const newHandle = handleFromUrl(response.url);
    if (!newHandle || newHandle === handle) return false;

    Wishlist.removeHandle(handle);
    Wishlist.addHandle(newHandle);
    return true;
  }

  #renderSkeleton(wrapper: HTMLElement): void {
    const template = this.querySelector<HTMLTemplateElement>(
      "template[data-wishlist-card-skeleton]",
    );
    wrapper.replaceChildren(
      template ? template.content.cloneNode(true) : document.createTextNode(""),
    );
  }

  #renderRetry(wrapper: HTMLElement, handle: string): void {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "wishlist-card-retry";
    button.textContent = this.dataset.retryLabel ?? "";
    button.addEventListener(
      "click",
      () => void this.#loadCard(wrapper, handle),
      {
        once: true,
      },
    );
    wrapper.replaceChildren(button);
  }
}

if (!customElements.get("wishlist-products")) {
  customElements.define("wishlist-products", WishlistProducts);
}
